import { useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Users2,
  FileText, 
  Target,
  BarChart2,
  BarChart3,
  Calendar,
  Settings,
  ChevronLeft,
  ChevronRight,
  Layers,
  Telescope,
  Server,
  UserCog,
  Sparkles,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGlobalStore } from '@/core/store';
import { usePlugins } from '@/core/PluginContext';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

// 图标映射
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'dashboard': LayoutDashboard,
  'users': Users,
  'users-2': Users2,
  'file-text': FileText,
  'target': Target,
  'bar-chart': BarChart3,
  'bar-chart-2': BarChart2,
  'calendar': Calendar,
  'settings': Settings,
  'layers': Layers,
  'telescope': Telescope,
  'search': Search,
  'server': Server,
  'user-cog': UserCog,
  'sparkles': Sparkles,
};

// 默认菜单项
const defaultMenuItems = [
  { id: 'dashboard', title: '仪表盘', icon: 'dashboard', path: '/dashboard', order: 0 },
  { id: 'accounts', title: '账号管理', icon: 'users', path: '/accounts', order: 1 },
  { id: 'content', title: '内容管理', icon: 'file-text', path: '/content', order: 2 },
  { id: 'tracks', title: '赛道管理', icon: 'target', path: '/tracks', order: 3 },
  { id: 'competitors', title: '竞品分析', icon: 'telescope', path: '/competitors', order: 4 },
  { id: 'clusters', title: '集群管理', icon: 'server', path: '/clusters', order: 5 },
  { id: 'analytics', title: '数据分析', icon: 'bar-chart', path: '/analytics', order: 6 },
  { id: 'schedule', title: '排期调度', icon: 'calendar', path: '/schedule', order: 7 },
  { id: 'team', title: '团队协作', icon: 'user-cog', path: '/team', order: 8 },
  { id: 'ai-studio', title: 'AI工作室', icon: 'sparkles', path: '/ai-studio', order: 9 },
];

// 系统菜单
const systemMenuItems = [
  { id: 'settings', title: '系统设置', icon: 'settings', path: '/settings' },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useGlobalStore();
  const { sidebarMenus } = usePlugins();

  // 合并默认菜单和插件菜单（去重）
  const allMenus = [...defaultMenuItems, ...sidebarMenus];
  const menuIds = new Set<string>();
  const menuItems = allMenus.filter(item => {
    if (menuIds.has(item.id)) return false;
    menuIds.add(item.id);
    return true;
  }).sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const renderMenuItem = (item: typeof menuItems[0]) => {
    const Icon = iconMap[item.icon || 'file-text'] || FileText;
    const isActive = location.pathname === item.path || 
                     location.pathname.startsWith(item.path + '/');

    const menuContent = (
      <Link
        to={item.path || '#'}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          isActive && 'bg-hermes-500 text-white hover:bg-hermes-600 hover:text-white',
          !isActive && 'text-sidebar-foreground/80'
        )}
      >
        <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-white')} />
        {!sidebarCollapsed && (
          <span className="text-sm font-medium truncate">{item.title}</span>
        )}
      </Link>
    );

    if (sidebarCollapsed) {
      return (
        <Tooltip key={item.id} delayDuration={0}>
          <TooltipTrigger asChild>
            {menuContent}
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-hermes-500">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.id}>{menuContent}</div>;
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-30 h-screen bg-sidebar border-r border-sidebar-border',
        'flex flex-col transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg gradient-hermes flex items-center justify-center">
            <span className="text-white font-bold text-lg">R</span>
          </div>
          {!sidebarCollapsed && (
            <div>
              <h1 className="font-display font-bold text-lg text-sidebar-foreground">
                RedMix
              </h1>
              <p className="text-xs text-sidebar-foreground/60">矩阵运营平台</p>
            </div>
          )}
        </div>
      </div>

      {/* 主菜单 */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {menuItems.map(renderMenuItem)}
        </nav>

        <Separator className="my-4 bg-sidebar-border" />

        {/* 系统菜单 */}
        <nav className="space-y-1">
          {systemMenuItems.map(renderMenuItem)}
        </nav>
      </ScrollArea>

      {/* 底部折叠按钮 */}
      <div className="p-3 border-t border-sidebar-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSidebar}
          className={cn(
            'w-full justify-center text-sidebar-foreground/60 hover:text-sidebar-foreground',
            'hover:bg-sidebar-accent'
          )}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span className="ml-2">收起菜单</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}

